import { useState } from 'react';
import { wordDiff } from '@/lib/wordDiff';
import { AlertTriangle, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CollabDiffEntry } from '../collabTypes';
import { FilePreviewDialog } from './PdfPreview';

const STATUS_TAG: Record<CollabDiffEntry['status'], { label: string; cls: string }> = {
  changed: { label: 'Edited', cls: 'bg-electric/12 text-brand-blue' },
  placeholder_filled: { label: 'Completed', cls: 'bg-state-warning-bg text-state-warning-text' },
  added: { label: 'Added field', cls: 'bg-state-success-bg text-state-success-text' },
};

// Commenting on one change. The box that used to sit here held its text in the page and only
// travelled if the reviewer pressed Send back — Approve discarded it silently, which is worse
// than not offering it. A comment now goes to the one audited Discussion, tagged with this
// change, the same way the Word redline's rail does it.
export interface DiffReviewControls {
  /** How many discussion comments already reference each change, keyed by its path. */
  commentCounts?: Record<string, number>;
  onComment: (path: string, label: string) => void;
}

/** A per-change Comment earns its place only when there is more than one change to point at.
 *  On a single-change revision (a replaced file) it names the only thing on screen and sits
 *  inches from the Discussion button — the same rule the Word redline's rail follows. */
function tagsWorthMaking(count: number): boolean {
  return count > 1;
}

// Download/preview links for a file-backed (upload) review: the released base file vs the
// working-copy file. ``view`` is always offered and always opens OUR viewer — the reviewer was
// the one screen still handing the file to a browser tab, and still hiding View for anything
// that is not a PDF, after every other screen stopped doing both. Bytes are opaque → no
// textual redline.
export interface FileSideLink {
  download: string;
  view: string;
  filename: string | null;
}
export interface FileLinks {
  current?: FileSideLink;
  proposed?: FileSideLink;
}

/** The extension, lower-cased — what a reader means by "the same kind of file". */
function extensionOf(name: string | null): string | null {
  const t = (name ?? '').trim();
  const i = t.lastIndexOf('.');
  return i > 0 ? t.slice(i + 1).toLowerCase() : null;
}

function FileSide({ name, link, accent, typeChanged }: {
  name: string | null; link?: FileSideLink; accent: boolean; typeChanged?: boolean;
}) {
  const [preview, setPreview] = useState<FileSideLink | null>(null);
  return (
    <div className="space-y-1.5">
      {preview && (
        <FilePreviewDialog
          open
          onOpenChange={(v) => { if (!v) setPreview(null); }}
          docRef=""
          title={preview.filename ?? name ?? 'Document'}
          fileUrl={preview.view}
          filename={preview.filename}
        />
      )}
      <p className={cn('truncate text-[13px]', accent ? 'font-medium text-navy' : 'text-muted')}>
        {name ?? 'No file'}
      </p>
      {typeChanged && (
        // A video replaced by a PDF is a different deliverable, not a revision of one. The trail
        // records it; the person deciding should see it at the moment of deciding.
        <p className="flex items-center gap-1 text-[12px] font-medium text-state-warning-text">
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden /> Different file type
        </p>
      )}
      {link && (
        <div className="flex gap-3">
          <a href={link.download} target="_blank" rel="noopener" className="text-[12px] font-medium text-brand-blue hover:underline">
            Download
          </a>
          <button
            type="button"
            onClick={() => setPreview(link)}
            className="text-[12px] font-medium text-brand-blue hover:underline"
          >
            View
          </button>
        </div>
      )}
    </div>
  );
}

// One side of a prose redline: the old view keeps removals (struck), the new
// view keeps additions (highlighted). Unchanged words stay for context.
function ProseSide({ oldText, newText, side }: { oldText: string; newText: string; side: 'old' | 'new' }) {
  const parts = wordDiff(oldText, newText).filter((p) => (side === 'old' ? p.type !== 'add' : p.type !== 'remove'));
  return (
    <p className="text-[13px] leading-relaxed text-navy">
      {parts.map((p, i) => (
        <span
          key={i}
          className={cn(
            p.type === 'add' && 'rounded bg-state-success-bg px-0.5 text-state-success-text',
            p.type === 'remove' && 'rounded bg-state-error-bg px-0.5 text-state-error-text line-through',
          )}
        >
          {p.text}
        </span>
      ))}
    </p>
  );
}

function CurrentSide({ e }: { e: CollabDiffEntry }) {
  if (e.status === 'placeholder_filled')
    return <p className="text-[13px] italic text-muted">Needs completion — left blank in the current version</p>;
  if (e.status === 'added' || e.old == null)
    return <p className="text-[13px] italic text-muted">Not in the current version</p>;
  if (e.kind === 'prose') return <ProseSide oldText={e.old} newText={e.new ?? ''} side="old" />;
  return <p className="text-[13px] text-muted">{e.old}</p>;
}

function ProposedSide({ e }: { e: CollabDiffEntry }) {
  if (e.kind === 'prose') return <ProseSide oldText={e.old ?? ''} newText={e.new ?? ''} side="new" />;
  return <p className="text-[13px] font-medium text-navy">{e.new}</p>;
}

function Row({
  e,
  releasedLabel,
  review,
  files,
  showComment,
}: {
  e: CollabDiffEntry;
  releasedLabel: string;
  review?: DiffReviewControls;
  files?: FileLinks;
  showComment: boolean;
}) {
  const tag = STATUS_TAG[e.status];
  // Marked when the discussion already has something to say about this change.
  const flagged = (review?.commentCounts?.[e.path] ?? 0) > 0;
  const isFile = e.kind === 'file';

  return (
    <li
      className={cn(
        'rounded-xl border p-3.5',
        flagged ? 'border-state-warning-border bg-state-warning-bg/30' : 'border-line bg-white',
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-navy">{e.field_label}</span>
        {e.field_context && <span className="text-[11.5px] text-muted">in {e.field_context}</span>}
        <span
          className={cn(
            'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            tag.cls,
          )}
        >
          {tag.label}
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-lg border border-line bg-line-soft/40 px-3 py-2">
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">{releasedLabel}</p>
          {isFile ? <FileSide name={e.old} link={files?.current} accent={false} /> : <CurrentSide e={e} />}
        </div>
        <div className="rounded-lg border border-electric/30 bg-electric/[0.04] px-3 py-2">
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-brand-blue">Proposed</p>
          {isFile
            ? <FileSide
                name={e.new}
                link={files?.proposed}
                accent
                typeChanged={!!e.old && !!e.new && extensionOf(e.old) !== extensionOf(e.new)}
              />
            : <ProposedSide e={e} />}
        </div>
      </div>
      {review && showComment && (
        <button
          type="button"
          onClick={() => review.onComment(e.path, e.field_label)}
          className="mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-muted hover:text-navy"
        >
          <MessageSquare className="size-3.5" aria-hidden />
          {(review.commentCounts?.[e.path] ?? 0) > 0
            ? `Comment (${review.commentCounts![e.path]})`
            : 'Comment'}
        </button>
      )}
    </li>
  );
}

export function VersionDiff({
  entries,
  releasedLabel = 'Current value',
  review,
  files,
}: {
  entries: CollabDiffEntry[];
  releasedLabel?: string;
  review?: DiffReviewControls;
  files?: FileLinks;
}) {
  if (entries.length === 0) {
    return <p className="py-8 text-center text-[13px] text-muted">No changes from the current version.</p>;
  }
  // Group changes by the section they belong to, preserving first-seen order, so
  // the reviewer reads edits in document context rather than as a flat list.
  const groups: { label: string; rows: CollabDiffEntry[] }[] = [];
  for (const e of entries) {
    const key = e.section_label || 'Other';
    const group = groups.find((g) => g.label === key);
    if (group) group.rows.push(e);
    else groups.push({ label: key, rows: [e] });
  }
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <section key={g.label} className="overflow-hidden rounded-2xl border border-line bg-white shadow-card">
          <header className="flex items-center justify-between gap-3 border-b border-line bg-line-soft/50 px-5 py-2.5">
            <h4 className="text-[13px] font-bold text-navy">{g.label}</h4>
            <span className="text-[11.5px] text-muted">
              {g.rows.length} change{g.rows.length === 1 ? '' : 's'}
            </span>
          </header>
          <ul className="space-y-3 px-4 py-4">
            {g.rows.map((e) => (
              <Row
                key={e.path}
                e={e}
                releasedLabel={releasedLabel}
                review={review}
                files={files}
                showComment={tagsWorthMaking(entries.length)}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
